import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { useUserProperties } from '../../hooks/useUserProperties';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Stepper from '../../components/base/Stepper';
import SegmentedControl from '../../components/base/SegmentedControl';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { fetchPlans, type SubscriptionPlanFromAPI } from '../../services/subscriptionService';
import { formatAmount } from '../../utils/numberFormat';
import { BrandRule } from '../../components/brand/BrandRule';

type BillingCycle = 'monthly' | 'yearly';
type PlanGroup = 'broker' | 'company' | 'developer';

// AI-related feature keys that should show "Coming Soon"
const AI_FEATURE_KEYS = ['aiTools', 'aiDescription', 'aiPricing', 'aiAnalytics', 'aiSearch'];

const FEATURE_LABELS: Record<string, { en: string; ar: string }> = {
  featuredListings: { en: 'Featured Listings', ar: 'إعلانات مميزة' },
  analytics: { en: 'Analytics', ar: 'التحليلات' },
  aiTools: { en: 'AI Tools', ar: 'أدوات الذكاء الاصطناعي' },
  customBranding: { en: 'Custom Branding', ar: 'علامة تجارية مخصصة' },
  dedicatedAccountManager: { en: 'Dedicated Account Manager', ar: 'مدير حساب مخصص' },
  logoOnImages: { en: 'Logo on Images', ar: 'شعار على الصور' },
  boostVisibility: { en: 'Boost Visibility', ar: 'رفع الظهور' },
  noCommission: { en: 'No Commission', ar: 'بدون عمولة' },
  apiAccess: { en: 'API Access', ar: 'الوصول لـ API' },
};

// A tier value («basic» / «advanced» / «premium») is shown translated after its
// label («التحليلات: متقدمة»); it used to be glued on raw («basicالتحليلات»).
const TIER_LABELS: Record<string, { en: string; ar: string }> = {
  basic: { en: 'Basic', ar: 'أساسية' },
  advanced: { en: 'Advanced', ar: 'متقدمة' },
  premium: { en: 'Premium', ar: 'مميزة' },
};

function renderFeatures(features: Record<string, unknown>, isRTL: boolean) {
  return Object.entries(features)
    .filter(([, value]) => value && value !== 'none' && value !== false && value !== 0)
    .map(([key, value]) => {
      const label = FEATURE_LABELS[key];
      if (!label) return null;
      const text = isRTL ? label.ar : label.en;
      const isAI = AI_FEATURE_KEYS.includes(key);
      const comingSoon = isAI ? (isRTL ? ' (قريباً)' : ' (coming soon)') : '';
      const tier = typeof value === 'string' ? TIER_LABELS[value] : undefined;
      const detail =
        typeof value === 'number'
          ? formatAmount(value)
          : tier
            ? (isRTL ? tier.ar : tier.en)
            : typeof value === 'string' && value !== 'true'
              ? value
              : '';
      return { key, text: `${text}${detail ? `: ${detail}` : ''}${comingSoon}`, isAI };
    })
    .filter(Boolean) as { key: string; text: string; isAI: boolean }[];
}

/** The real saving of paying yearly, in whole percent (0 when there is none). */
function yearlySaving(plan: SubscriptionPlanFromAPI): number {
  if (!plan.priceMonthly || !plan.priceYearly) return 0;
  return Math.max(0, Math.round((1 - plan.priceYearly / (plan.priceMonthly * 12)) * 100));
}

/** Which tab a plan belongs to. Anything that is neither company nor developer is a broker plan. */
function planGroup(plan: SubscriptionPlanFromAPI): PlanGroup {
  if (plan.planType === 'company') return 'company';
  if (plan.planType === 'developer') return 'developer';
  return 'broker';
}

const GROUP_LABELS: Record<PlanGroup, { en: string; ar: string; icon: string }> = {
  broker: { en: 'Individual broker', ar: 'وسيط فردي', icon: 'ri-user-line' },
  company: { en: 'Company', ar: 'شركة عقارية', icon: 'ri-building-2-line' },
  developer: { en: 'Developer', ar: 'مطوّر عقاري', icon: 'ri-building-4-line' },
};

const SECTION_TITLE = 'text-[1.25rem] font-semibold text-[var(--pv-color-primary)]';

/**
 * Advertiser subscriptions (owner, 2026-10-07): one header, the plans as equal
 * cards in the wasalt.sa pattern (name, who it is for, the price, what it
 * includes, one call to action at the bottom), no icon tile per plan and no
 * second comparison table repeating the cards. Two steps: pick a plan, then its
 * details and subscribe (the Stepper says which).
 */
export default function MembershipPage() {
  const { t, i18n } = useTranslation(['membership', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    subscribe: saveSubscription,
    subscription,
    isSubscribed,
    daysRemaining,
    loading: subscriptionLoading,
  } = useSubscription();
  // subscription.listingsUsed is a stale field on the UserSubscription row that
  // is not kept in sync with property creation. Derive the real remaining
  // count from the user's actual properties instead.
  const { total: realListingsUsed } = useUserProperties();
  const listingsRemaining = subscription
    ? Math.max(0, subscription.maxListings - realListingsUsed)
    : 0;
  const isRTL = i18n.dir() === 'rtl';

  // The page is a two-step flow: pick a plan, then its details and subscribe.
  const flowSteps = [
    { id: 'plan', label: t('steps.choosePlan', 'Choose a plan') },
    { id: 'details', label: t('steps.details', 'Details & subscribe') },
  ];
  const flowStepsLabel = t('steps.label', 'Subscription steps');

  // Determine account type for plan filtering
  const accountType = ((): PlanGroup | null => {
    if (!user) return null;
    const meta = user.metadata;
    if (meta) {
      try {
        const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta;
        if (parsed?.accountType === 'company') return 'company';
        if (parsed?.accountType === 'developer') return 'developer';
        if (parsed?.accountType === 'broker') return 'broker';
      } catch { /* ignore */ }
    }
    if (user.role === 'COMPANY_ADMIN') return 'company';
    if (user.role === 'BROKER' || user.role === 'AGENT') return 'broker';
    return null;
  })();

  const [plans, setPlans] = useState<SubscriptionPlanFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanFromAPI | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<PlanGroup>(accountType ?? 'broker');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Always fetch all plans - frontend handles tab filtering
        const data = await fetchPlans();
        setPlans(data);
      } catch {
        setError(isRTL ? 'فشل في تحميل الباقات' : 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isRTL]);

  const subscriptionRequired = searchParams.get('reason') === 'required';

  // Plans by tab. Developer plans had no tab and showed among the broker plans.
  const groups: Record<PlanGroup, SubscriptionPlanFromAPI[]> = { broker: [], company: [], developer: [] };
  plans.forEach((plan) => groups[planGroup(plan)].push(plan));
  const groupsWithPlans = (Object.keys(groups) as PlanGroup[]).filter((group) => groups[group].length > 0);

  // A known broker / company / developer sees only their plans (no switcher: a
  // plan of another type would not change what the account is). Otherwise the
  // chosen tab, or the first tab that has plans.
  const effectiveTab: PlanGroup =
    accountType && groups[accountType].length > 0
      ? accountType
      : groups[activeTab].length > 0
        ? activeTab
        : groupsWithPlans[0] ?? activeTab;
  const activePlans = groups[effectiveTab];

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/signin?redirect=/membership');
      return;
    }
    if (!selectedPlan) return;

    setIsSubmitting(true);
    try {
      await saveSubscription({
        planId: selectedPlan.id,
        billingCycle,
      });
      navigate('/properties/new');
    } catch (err) {
      console.error('Subscription error:', err);
      alert(isRTL ? 'فشل في معالجة الاشتراك. يرجى المحاولة مرة أخرى.' : 'Failed to process subscription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setSelectedPlan(null);
  };

  const planName = (plan: SubscriptionPlanFromAPI) => (isRTL ? plan.nameAr || plan.name : plan.name);
  const planDescription = (plan: SubscriptionPlanFromAPI) =>
    isRTL ? plan.descriptionAr || plan.description : plan.description;
  // «100 إعلان · 5 مستخدمين · 99 نقطة»
  const planLimits = (plan: SubscriptionPlanFromAPI) =>
    [
      plan.maxListings ? `${formatAmount(plan.maxListings)} ${isRTL ? 'إعلان' : 'listings'}` : '',
      plan.maxSubUsers ? `${formatAmount(plan.maxSubUsers)} ${isRTL ? 'مستخدمين' : 'users'}` : '',
      plan.includedCoins > 0 ? `${formatAmount(plan.includedCoins)} ${isRTL ? 'نقطة' : 'coins'}` : '',
    ].filter(Boolean).join(' · ');

  const featureList = (plan: SubscriptionPlanFromAPI) => {
    const features = renderFeatures(plan.features || {}, isRTL);
    if (features.length === 0) return null;
    return (
      <ul role="list" className="m-0 list-none space-y-2.5 p-0">
        {features.map((feature) => (
          <li key={feature.key} className="flex items-start gap-2.5 text-[0.875rem]">
            <i
              className={`mt-0.5 shrink-0 text-base leading-none ${feature.isAI ? 'ri-time-line text-[#8C6A0B]' : 'ri-check-line text-[var(--pv-color-primary)]'}`}
              aria-hidden="true"
            ></i>
            <span className={feature.isAI ? 'text-[var(--pv-color-text-muted)]' : 'text-[var(--pv-color-text)]'}>{feature.text}</span>
          </li>
        ))}
      </ul>
    );
  };

  const price = (amount: number, period: BillingCycle, size: 'md' | 'lg') => (
    <p className="m-0 flex items-baseline gap-1.5">
      <SaudiRiyalSymbol
        className={`shrink-0 self-center text-[var(--pv-color-primary)] ${size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'}`}
        aria-hidden
      />
      <span className={`font-bold leading-none text-[var(--pv-color-primary)] tabular-nums ${size === 'lg' ? 'text-[2.5rem]' : 'text-[2rem]'}`}>
        {formatAmount(amount)}
      </span>
      <span className="sr-only">{t('common:currency.name')}</span>
      <span className="text-[0.875rem] text-[var(--pv-color-text-muted)]">
        / {period === 'monthly' ? (isRTL ? 'شهر' : 'month') : (isRTL ? 'سنة' : 'year')}
      </span>
    </p>
  );

  const helpLink = (text: string, link: string) => (
    <div className="mt-12 text-center">
      <p className="text-[0.9375rem] text-[var(--pv-color-text-muted)]">{text}</p>
      <a
        href="/contact"
        className="mt-1 inline-flex items-center gap-1 text-[0.9375rem] font-semibold text-[var(--pv-color-primary)] underline-offset-4 hover:underline"
      >
        {link}
        <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`} aria-hidden="true"></i>
      </a>
    </div>
  );

  // Step 2: Plan details view
  if (selectedPlan) {
    const saving = yearlySaving(selectedPlan);
    const amount = billingCycle === 'monthly' ? selectedPlan.priceMonthly : selectedPlan.priceYearly;
    const limits = planLimits(selectedPlan);

    return (
      <div className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]" dir={i18n.dir()}>
        <Navigation />

        <main className="mx-auto max-w-xl px-4 pb-20 pt-10">
          <Stepper
            steps={flowSteps}
            currentStep={1}
            ariaLabel={flowStepsLabel}
            className="mx-auto mb-8 max-w-md"
          />

          <button
            type="button"
            onClick={handleBack}
            className="mb-6 inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[var(--pv-color-text-muted)] transition-colors hover:text-[var(--pv-color-primary)]"
          >
            <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line`} aria-hidden="true"></i>
            <span>{isRTL ? 'رجوع' : 'Back'}</span>
          </button>

          <h1 className="text-[1.75rem] font-bold text-[var(--pv-color-primary)]">{planName(selectedPlan)}</h1>
          <BrandRule className="mt-3" />
          {planDescription(selectedPlan) && (
            <p className="mt-1.5 text-[0.9375rem]">{planDescription(selectedPlan)}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <SegmentedControl<BillingCycle>
              value={billingCycle}
              onChange={setBillingCycle}
              options={[
                { value: 'monthly', label: isRTL ? 'شهري' : 'Monthly' },
                { value: 'yearly', label: isRTL ? 'سنوي' : 'Yearly' },
              ]}
              ariaLabel={isRTL ? 'دورة الدفع' : 'Billing cycle'}
              size="sm"
            />
            {saving > 0 && (
              <span className="text-[0.875rem] font-medium text-[var(--pv-color-primary)]">
                {isRTL ? `وفّر ${saving}٪ مع الاشتراك السنوي` : `Save ${saving}% with yearly billing`}
              </span>
            )}
          </div>

          <div className="mt-5 rounded-[16px] border border-[var(--pv-border-soft)] p-6">
            {price(amount, billingCycle, 'lg')}
            {limits && <p className="mt-3 text-[0.9375rem] text-[var(--pv-color-text)]">{limits}</p>}

            <Button className="mt-6 w-full" onClick={handleSubscribe} disabled={isSubmitting} loading={isSubmitting}>
              {isRTL ? 'اشترك الآن' : 'Subscribe Now'}
            </Button>

            {renderFeatures(selectedPlan.features || {}, isRTL).length > 0 && (
              <div className="mt-6 border-t border-[var(--pv-border-subtle)] pt-6">{featureList(selectedPlan)}</div>
            )}

            <p className="mt-6 flex items-start gap-2 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
              <i className="ri-information-line shrink-0 text-base leading-none" aria-hidden="true"></i>
              {isRTL ? 'يتم إغلاق الإعلانات عند انتهاء الاشتراك.' : 'Listings are closed when the subscription ends.'}
            </p>
          </div>

          {helpLink(isRTL ? 'هل تحتاج مساعدة؟' : 'Need help?', isRTL ? 'تواصل معنا' : 'Contact us')}
        </main>

        <Footer />
      </div>
    );
  }

  // Step 1: Plan selection
  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]" dir={i18n.dir()}>
      <Navigation />

      <main className="pb-20">
        {/* Sent here by a feature that needs a subscription */}
        {subscriptionRequired && (
          <div className="border-b border-[#FBE5A3] bg-[#FEF8E8] px-4 py-3">
            <p className="mx-auto flex max-w-5xl items-center gap-2 text-[0.9375rem] font-medium text-[#8C6A0B]">
              <i className="ri-information-line shrink-0 text-[1.125rem] leading-none" aria-hidden="true"></i>
              {isRTL
                ? 'يرجى اختيار خطة اشتراك للوصول إلى هذه الميزة.'
                : 'Please choose a subscription plan to access this feature.'}
            </p>
          </div>
        )}

        <div className="mx-auto max-w-5xl px-4 pt-10">
          <header className="text-center">
            <h1 className="text-[1.75rem] font-bold text-[var(--pv-color-primary)] sm:text-[2.25rem]">
              {isRTL ? 'اشتراكات المعلنين' : 'Advertiser Subscriptions'}
            </h1>
            <BrandRule className="mx-auto mt-4" />
            <p className="mt-2 text-[1rem]">
              {isRTL ? 'اختر الباقة المناسبة لك للبدء في نشر إعلاناتك' : 'Choose the right plan to start listing your properties'}
            </p>
          </header>

          <Stepper
            steps={flowSteps}
            currentStep={0}
            ariaLabel={flowStepsLabel}
            className="mx-auto mb-10 mt-8 max-w-md"
          />

          {/* The plan the account is on now */}
          {isSubscribed && subscription && !subscriptionLoading && (
            <section className="mb-12 rounded-[16px] border border-[var(--pv-color-primary)] p-6">
              <h2 className={SECTION_TITLE}>{isRTL ? 'خطتك الحالية' : 'Your current plan'}</h2>
              <dl className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-[0.875rem]">{isRTL ? 'الإعلانات المتبقية' : 'Listings remaining'}</dt>
                  <dd className="m-0 mt-1 text-[1.5rem] font-bold text-[var(--pv-color-text)] tabular-nums">{formatAmount(listingsRemaining)}</dd>
                </div>
                <div>
                  <dt className="text-[0.875rem]">{isRTL ? 'الأيام المتبقية' : 'Days remaining'}</dt>
                  <dd className="m-0 mt-1 text-[1.5rem] font-bold text-[var(--pv-color-text)] tabular-nums">{formatAmount(daysRemaining)}</dd>
                </div>
              </dl>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button onClick={() => navigate('/properties/new')}>
                  <i className={`ri-add-line ${isRTL ? 'ml-2' : 'mr-2'}`} aria-hidden="true"></i>
                  {isRTL ? 'إنشاء إعلان جديد' : 'Create New Listing'}
                </Button>
                <Button variant="outline" onClick={() => navigate('/profile')}>
                  {isRTL ? 'إدارة الحساب' : 'Manage account'}
                </Button>
              </div>
            </section>
          )}

          <h2 className={`${SECTION_TITLE} text-center`}>
            {isSubscribed
              ? (isRTL ? 'تغيير أو ترقية الباقة' : 'Change or upgrade your plan')
              : (isRTL ? 'اختر باقتك' : 'Select your plan')}
          </h2>

          {/* Who the plans are for: a switcher only while the account type is
              unknown (e.g. a visitor). A known broker / company / developer
              sees only their plans: subscribing to another type's plan would
              not change what the account is. */}
          {!loading && !error && !accountType && groupsWithPlans.length > 1 && (
            <div className="mt-5 flex justify-center">
              <SegmentedControl<PlanGroup>
                value={effectiveTab}
                onChange={setActiveTab}
                options={groupsWithPlans.map((group) => ({
                  value: group,
                  label: isRTL ? GROUP_LABELS[group].ar : GROUP_LABELS[group].en,
                  icon: GROUP_LABELS[group].icon,
                }))}
                ariaLabel={isRTL ? 'نوع الحساب' : 'Account type'}
                size="sm"
              />
            </div>
          )}
          {!loading && !error && accountType && activePlans.length > 0 && (
            <p className="mt-3 text-center text-[0.9375rem]">
              {isRTL ? `باقات ${GROUP_LABELS[accountType].ar}` : `${GROUP_LABELS[accountType].en} plans`}
            </p>
          )}

          {loading && (
            <ul role="list" aria-hidden="true" className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <li key={i} className="h-[26rem] animate-pulse rounded-[16px] bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
              ))}
            </ul>
          )}

          {error && !loading && (
            <p className="mt-10 text-center text-[0.9375rem]">{error}</p>
          )}

          {!loading && !error && plans.length === 0 && (
            <p className="mt-10 text-center text-[0.9375rem]">
              {isRTL ? 'لا توجد باقات متاحة حالياً' : 'No plans available at the moment'}
            </p>
          )}

          {/* The plans: equal cards, the call to action at the same height in each. */}
          {!loading && !error && activePlans.length > 0 && (
            <div
              className={`mt-8 grid items-stretch gap-5 ${
                activePlans.length === 1
                  ? 'mx-auto max-w-md'
                  : activePlans.length === 2
                    ? 'mx-auto max-w-3xl grid-cols-1 md:grid-cols-2'
                    : 'grid-cols-1 md:grid-cols-3'
              }`}
            >
              {activePlans.map((plan) => {
                const saving = yearlySaving(plan);
                const limits = planLimits(plan);
                return (
                  // One action per card: the «اختيار الباقة» button, whose ::after
                  // stretches over the card so a click anywhere on it chooses.
                  <article
                    key={plan.id}
                    className={`relative flex h-full flex-col rounded-[16px] border bg-[var(--pv-color-surface)] p-6 transition-shadow duration-200 hover:shadow-brand-md ${
                      plan.isPopular ? 'border-[var(--pv-color-primary)]' : 'border-[var(--pv-border-soft)]'
                    }`}
                  >
                    <div className="flex min-h-[1.75rem] items-start justify-between gap-2">
                      <h3 className="text-[1.25rem] font-semibold text-[var(--pv-color-text)]">{planName(plan)}</h3>
                      {plan.isPopular && (
                        <span className="shrink-0 rounded-full bg-[#FFC107] px-2.5 py-1 text-[0.75rem] font-semibold leading-none text-[#4A3800]">
                          {isRTL ? 'الأكثر شعبية' : 'Most popular'}
                        </span>
                      )}
                    </div>
                    {planDescription(plan) && <p className="mt-1 text-[0.875rem]">{planDescription(plan)}</p>}

                    <div className="mt-5">{price(plan.priceMonthly, 'monthly', 'md')}</div>
                    {plan.priceYearly > 0 && (
                      <p className="mt-1.5 text-[0.8125rem]">
                        {isRTL
                          ? `أو ${formatAmount(plan.priceYearly)} سنوياً${saving > 0 ? ` (وفّر ${saving}٪)` : ''}`
                          : `or ${formatAmount(plan.priceYearly)} a year${saving > 0 ? ` (save ${saving}%)` : ''}`}
                      </p>
                    )}
                    {limits && <p className="mt-4 text-[0.9375rem] text-[var(--pv-color-text)]">{limits}</p>}

                    {renderFeatures(plan.features || {}, isRTL).length > 0 && (
                      <div className="mt-5 border-t border-[var(--pv-border-subtle)] pt-5">{featureList(plan)}</div>
                    )}

                    <div className="mt-auto pt-6">
                      <button
                        type="button"
                        onClick={() => setSelectedPlan(plan)}
                        aria-label={`${isRTL ? 'اختيار الباقة' : 'Choose this plan'}: ${planName(plan)}`}
                        className={`flex h-11 w-full items-center justify-center rounded-full text-[0.9375rem] font-semibold transition-colors after:absolute after:inset-0 after:rounded-[16px] after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30 ${
                          plan.isPopular
                            ? 'bg-[var(--pv-color-primary)] text-white hover:opacity-90 dark:text-[var(--pv-color-surface)]'
                            : 'border border-[var(--pv-border-strong)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]'
                        }`}
                      >
                        {isRTL ? 'اختيار الباقة' : 'Choose this plan'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {helpLink(
            isRTL ? 'هل تحتاج مساعدة في اختيار الباقة المناسبة؟' : 'Need help choosing the right plan?',
            isRTL ? 'تواصل مع فريق المبيعات' : 'Contact our sales team',
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
